import { Link } from "@tanstack/react-router";
import { Bell, CircleUserRound, LogOut, PencilRuler, UserCog } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/services/auth/auth-context";
import { NotificationRoleType, useNotifications } from "@/services/entities/use-notifications";

export default function NavBarProfileLogin() {
  const { authUser, isAdmin, isTutor, signOut, dbProfile, familyId, isClientOnly } = useAuth();
  const { notifications } = useNotifications(NotificationRoleType.ALL);

  const DashboardLinks = () => {
    return (
      <>
        {isTutor && (
          <DropdownMenuItem asChild>
            <Link to="/tutordashboard" className="flex items-center w-full">
              <PencilRuler className="w-4 h-4 mr-2" />
              <span>Tutor Dashboard</span>
            </Link>
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admindashboard" className="flex items-center w-full">
              <UserCog className="w-4 h-4 mr-2" />
              <span>Admin</span>
            </Link>
          </DropdownMenuItem>
        )}
        {(isClientOnly || familyId) && (
          <DropdownMenuItem asChild>
            <Link to="/clientdashboard" className="flex items-center w-full">
              <PencilRuler className="w-4 h-4 mr-2" />
              <span>Client Dashboard</span>
            </Link>
          </DropdownMenuItem>
        )}
      </>
    );
  };

  const logOutUser = async () => {
    await signOut();
  };

  const getFirstInitial = (name: string) => {
    if (!name) {
      return "";
    }
    const [firstName] = name.split(" ");
    if (!firstName || firstName.length === 0) {
      return "";
    }
    return firstName[0].toUpperCase();
  };

  const effectiveName = dbProfile?.staffData
    ? `${dbProfile.staffData.firstName} ${dbProfile.staffData.lastName}`
    : dbProfile?.userData
      ? `${dbProfile.userData.firstName} ${dbProfile.userData.lastName}`
      : authUser?.user_metadata?.email;

  return (
    <>
      {authUser ? (
        <DropdownMenu>
          <DropdownMenuTrigger className="focus:outline-none">
            <Avatar className="h-9 w-9 cursor-pointer">
              {effectiveName && (
                <AvatarImage
                  src={`https://api.dicebear.com/9.x/initials/svg?seed=${effectiveName}`}
                />
              )}
              <AvatarFallback>
                {effectiveName ? (
                  getFirstInitial(effectiveName)
                ) : (
                  <CircleUserRound className="h-5 w-5" />
                )}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DashboardLinks />
            <DropdownMenuItem asChild>
              <Link to="/notifications" className="flex items-center w-full relative">
                <Bell className="h-4 w-4 mr-2" />
                <span>Notifications</span>
                {notifications && notifications.length > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-background">
                    {notifications.length}
                  </span>
                )}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logOutUser}>
              <div className="flex items-center w-full">
                <LogOut className="h-4 w-4 mr-2" />
                <span>Logout</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Link
          to="/login"
          className="inline-flex items-center px-4 py-2 rounded-md bg-slate-50  text-black  hover:bg-primary/90 hover:text-white border border-gray-600"
        >
          Login
        </Link>
      )}
    </>
  );
}
